import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  HStack,
  SimpleGrid,
  useToast,
  FormErrorMessage,
  IconButton,
  Icon,
  Stack,
  Spinner,
  useDisclosure,
  Checkbox,
  Link,
} from '@chakra-ui/react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaCreditCard, FaApple, FaBitcoin, FaTimes } from 'react-icons/fa';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSubscription } from '../../context/SubscriptionContext';
import SubscriptionPolicyModal from '../policies/SubscriptionPolicyModal';
import CustomSelect from '../../utils/CustomSelect';
import PaymentRequestButton from '../Payments/PaymentRequestButton';

const PaymentMethodSelect = ({ selectedPaymentMethod, setSelectedPaymentMethod }) => (
  <FormControl w="100%" mt={4} bg="secondary.900" p={3} borderRadius="md">
    <FormLabel fontSize="lg" fontWeight="bold" color="primary.100">
      Payment Method
    </FormLabel>
    <RadioGroup value={selectedPaymentMethod} onChange={setSelectedPaymentMethod}>
      <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="card" colorScheme="blue">
            <HStack>
              <Icon as={FaCreditCard} boxSize={5} />
              <Text color="primary.100">Card</Text>
            </HStack>
          </Radio>
        </Box>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="applePay" colorScheme="blue">
            <HStack>
              <Icon as={FaApple} boxSize={5} />
              <Text color="primary.100">Apple Pay</Text>
            </HStack>
          </Radio>
        </Box>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="cryptoPay" colorScheme="blue">
            <HStack>
              <Icon as={FaBitcoin} boxSize={5} />
              <Text color="primary.100">Crypto Pay</Text>
            </HStack>
          </Radio>
        </Box>
      </Stack>
    </RadioGroup>
  </FormControl>
);

const CheckoutForm = ({ subscription, discountCode, onSuccess, discount, finalPrice, navigate }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [paymentMethodSelected, setPaymentMethodSelected] = useState('');
  const [isAddingNewMethod, setIsAddingNewMethod] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const stripe = useStripe();
  const elements = useElements();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { createSubscription, upgradeSubscription, createPaymentMethod, getPaymentMethods, paymentMethods } = useSubscription();
  const { register, handleSubmit, formState: { errors }, control } = useForm();

  // detect free subscription
  const isFree = subscription?.price === 0;

  useEffect(() => {
    if (!isFree) {
      getPaymentMethods().catch(err => {
        toast({ title: 'Error fetching payment methods', description: err.message, status: 'error', duration: 5000, isClosable: true });
      });
    }
  }, [getPaymentMethods, isFree, toast]);

  const COUNTRIES = [
    { name: 'United States', code: 'US' },
    { name: 'Canada', code: 'CA' },
    { name: 'United Kingdom', code: 'GB' },
    { name: 'Australia', code: 'AU' },
    { name: 'Germany', code: 'DE' },
    { name: 'France', code: 'FR' },
    { name: 'India', code: 'IN' },
    { name: 'Japan', code: 'JP' },
    { name: 'Brazil', code: 'BR' },
    { name: 'Mexico', code: 'MX' },
    { name: 'South Africa', code: 'ZA' },
    { name: 'Singapore', code: 'SG' },
  ];

  const CARD_OPTIONS = {
    style: {
      base: { fontSize: '16px', color: '#424770', fontFamily: 'Poppins, sans-serif', '::placeholder': { color: '#aab7c4' }, padding: '12px', backgroundColor: 'transparent', borderRadius: '6px' },
      invalid: { color: '#ff4c4c', iconColor: '#ff4c4c' },
      complete: { color: '#4caf50' },
    }, hidePostalCode: true, disableLink: true,
  };

  const onSubmit = async data => {
    if (!agreedToTerms) {
      toast({ title: 'Please agree to policy', status: 'error', duration: 5000, isClosable: true });
      return;
    }
    let paymentMethodId = paymentMethodSelected;
    setIsProcessing(true);

    if (!isFree) {
      if (!stripe || !elements) {
        toast({ title: 'Stripe.js not loaded', status: 'error', duration: 5000, isClosable: true });
        setIsProcessing(false);
        return;
      }
      if (!isAddingNewMethod && !paymentMethodSelected) {
        toast({ title: 'Choose or add payment method', status: 'error', duration: 5000, isClosable: true });
        setIsProcessing(false);
        return;
      }
      if (isAddingNewMethod && selectedPaymentMethod === 'card') {
        const card = elements.getElement(CardElement);
        if (!card) { setIsProcessing(false); return; }
        try {
          paymentMethodId = await createPaymentMethod(card, stripe, {
            name: data.cardName,
            address: { line1: data.billingAddress, city: data.city, state: data.state, postal_code: data.postalCode, country: data.country }
          });
        } catch { setIsProcessing(false); return; }
      }
    }

    setLoading(true);
    try {
      if (isFree) {
        await createSubscription({ priceId: subscription.id });
      } else if (subscription.isUpgrade) {
        await upgradeSubscription({ subscriptionId: subscription.current_subscription_id, newPriceId: subscription.id, coupon: discount?.is_valid ? discount.coupon_code : null });
      } else {
        await createSubscription({ paymentMethodId, priceId: subscription.id, coupon: discount?.is_valid ? discount.coupon_code : null });
      }
      toast({ title: 'Subscription Successful', status: 'success', duration: 5000, isClosable: true });
      onSuccess?.();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsProcessing(false);
      setLoading(false);
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} p={{ base:1, md:6 }} bg="dark.500" borderRadius="md">
      {isFree && <Box bg="green.50" p={4} mb={4} borderRadius="md"><Text color="green.600" textAlign="center">Free plan selected. No payment required.</Text></Box>}

      {!isFree && (
        <FormControl>
          <FormLabel color="primary.100">Select Payment Method</FormLabel>
          <CustomSelect value={paymentMethodSelected} onChange={val=>{ setPaymentMethodSelected(val); setIsAddingNewMethod(val==='new'); }}>
            {paymentMethods.map(m => <option key={m.id} value={m.id}>{m.card.brand.toUpperCase()} ****{m.card.last4}</option>)}
            <option value="new">Add New Payment Method</option>
          </CustomSelect>
        </FormControl>
      )}

      {!isFree && isAddingNewMethod && <>
        <PaymentMethodSelect selectedPaymentMethod={selectedPaymentMethod} setSelectedPaymentMethod={setSelectedPaymentMethod} />
        
        {/* Apple Pay and Google Pay Button */}
        <Box mt={4}>
          <PaymentRequestButton
            amount={Math.round(finalPrice * 100)} // Convert to cents
            currency="usd"
            country="US"
            label={`${subscription?.name} - $${finalPrice.toFixed(2)}`}
            couponCode={discountCode}
            onPaymentSuccess={(paymentIntent, breakdown) => {
              console.log('Payment successful:', paymentIntent, breakdown);
              // Handle subscription success - you may need to call your subscription creation endpoint
              navigate('/dashboard');
            }}
            onPaymentError={(error) => {
              console.error('Payment error:', error);
            }}
          />
        </Box>

        {/* Divider between Apple Pay/Google Pay and Card payment */}
        <Box textAlign="center" my={4} position="relative">
          <Text
            bg="dark.900"
            px={3}
            color="gray.500"
            fontSize="sm"
            position="relative"
            zIndex={1}
          >
            OR
          </Text>
          <Box
            position="absolute"
            top="50%"
            left={0}
            right={0}
            height="1px"
            bg="gray.300"
            zIndex={0}
          />
        </Box>

        {selectedPaymentMethod==='card' && <>
          <FormControl isInvalid={errors.cardName} mt={4}><FormLabel>Name on Card</FormLabel><Input {...register('cardName',{required:true})} variant="filled" bg="dark.100"/><FormErrorMessage>Name required</FormErrorMessage></FormControl>
          <FormControl isInvalid={errors.billingAddress} mt={4}><FormLabel>Billing Address</FormLabel><Input {...register('billingAddress',{required:true})} variant="filled" bg="dark.100"/><FormErrorMessage>Address required</FormErrorMessage></FormControl>
          <SimpleGrid columns={{base:1,md:2}} spacing={4} mt={4}>
            <FormControl isInvalid={errors.city}><FormLabel>City</FormLabel><Input {...register('city',{required:true})} variant="filled" bg="dark.100"/><FormErrorMessage>City required</FormErrorMessage></FormControl>
            <FormControl isInvalid={errors.state}><FormLabel>State</FormLabel><Input {...register('state',{required:true})} variant="filled" bg="dark.100"/><FormErrorMessage>State required</FormErrorMessage></FormControl>
            <FormControl isInvalid={errors.country}><FormLabel>Country</FormLabel><Controller name="country" control={control} rules={{required:true}} defaultValue="" render={({field})=><CustomSelect {...field} variant="filled" bg="dark.100">{COUNTRIES.map(c=><option key={c.code} value={c.code}>{c.name}</option>)}</CustomSelect>}/><FormErrorMessage>Country required</FormErrorMessage></FormControl>
            <FormControl isInvalid={errors.postalCode}><FormLabel>ZIP/Postal Code</FormLabel><Input {...register('postalCode',{required:true})} variant="filled" bg="dark.100"/><FormErrorMessage>ZIP required</FormErrorMessage></FormControl>
          </SimpleGrid>
          <FormControl mt={4}><FormLabel>Card Details</FormLabel><Box p={4} bg="white" borderRadius="md"><CardElement options={CARD_OPTIONS}/></Box></FormControl>
        </>}
      </>}

      <FormControl display="flex" alignItems="center" mt={4} isInvalid={!agreedToTerms}>
        <Checkbox isChecked={agreedToTerms} onChange={e=>setAgreedToTerms(e.target.checked)}/>
        <Text ml={2}>I agree to the <Link color="blue.400" onClick={onOpen} textDecoration="underline">subscription policy</Link></Text>
      </FormControl>
      <SubscriptionPolicyModal isOpen={isOpen} onClose={onClose}/> 

      <Button mt={6} w="full" colorScheme="teal" type="submit" isLoading={loading}>
        {isProcessing ? <Spinner/> : isFree ? 'Confirm Free Subscription' : 'Confirm & Pay'}
      </Button>
    </Box>
  );
};

const SubscriptionCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { calculateProration, validateCoupon, loading, fetchUserSubscription } = useSubscription();
  const subscription = location.state?.subscription;
  const [discount, setDiscount] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [showDiscount, setShowDiscount] = useState(false);
  const [finalPrice, setFinalPrice] = useState(subscription?.price ?? 0);
  const [prorationAmount, setProrationAmount] = useState(0);

  useEffect(() => { if (!subscription) navigate('/subscriptions'); },[subscription,navigate]);
  useEffect(() => { if (subscription?.isUpgrade) calculateProration({currentSubscriptionId:subscription.current_subscription_id,newPriceId:subscription.id})
    .then(d=>{setProrationAmount(d.proration_amount);setFinalPrice(d.total_due);})
    .catch(console.error);
  },[subscription,calculateProration]);

  const applyDiscount = async ()=>{
    try { const res=await validateCoupon(discountCode,subscription.id,subscription.price); setDiscount(res); if(res.is_valid){ setFinalPrice(res.percent_off? (finalPrice*(100-res.percent_off))/100 : Math.max(0,finalPrice-res.amount_off)); setShowDiscount(true);} }
    catch{ setShowDiscount(false);}  
  };
  const handleSuccess = async () =>{ await fetchUserSubscription(); navigate('/subscriptions'); };
  const isFree = subscription?.price===0;
  if(!subscription) return null;

  return (
    <Box py={10} px={{base:2,md:10}} bg="dark.200" minH="100vh">
      <Box maxW="1200px" mx="auto">
        <Heading size="xl" mb={8} textAlign="center">Checkout</Heading>
        <SimpleGrid columns={{base:1,md:2}} spacing={8}>
          {loading?
            <Flex justify="center"><Spinner/></Flex>:
            <Box bg="dark.500" p={6} borderRadius="lg" color="primary.100">
              <Heading size="lg" mb={4} color="white" textAlign="center">Subscription Details</Heading>
              <Flex justify="space-between" py={2}><Text fontWeight="bold">Plan:</Text><Text>{subscription.name}</Text></Flex>
              <Flex justify="space-between" py={2}><Text fontWeight="bold">Price:</Text><Text>{isFree? <Text color="green.300">Free</Text> : `$${subscription.price.toFixed(2)}`}</Text></Flex>
              {subscription.isUpgrade && <Flex justify="space-between" py={2}><Text fontWeight="bold">Proration:</Text><Text>${prorationAmount.toFixed(2)}</Text></Flex>}
              {!subscription.isUpgrade && subscription.price>0 && <Box mt={4}>
                <FormControl><FormLabel>Discount Code</FormLabel><Flex><Input value={discountCode} onChange={e=>setDiscountCode(e.target.value)} placeholder="Code" variant="filled"/><Button ml={2} onClick={applyDiscount}>Apply</Button></Flex></FormControl>
                {showDiscount && discount && <Flex justify="space-between" mt={2} bg="secondary.100" p={2} align="center" borderRadius="md"><Text>Applied: {discount.name} ({discount.percent_off?`${discount.percent_off}%`:`$${(discount.amount_off/100).toFixed(2)}`} off)</Text><IconButton size="sm" icon={<FaTimes/>} onClick={()=>{setShowDiscount(false);setFinalPrice(subscription.price);setDiscount(null);}}/></Flex>}
              </Box>}
              <Flex justify="space-between" mt={6} bg="dark.100" p={2} borderRadius="md"><Text fontWeight="bold">Final Price:</Text><Text fontWeight="bold">{isFree? 'Free' : `$${finalPrice.toFixed(2)}`}</Text></Flex>
            </Box>
          }
          <Box bg="dark.500" p={6} borderRadius="lg">
            <Heading size="lg" mb={4}>Payment Information</Heading>
            <CheckoutForm subscription={subscription} discountCode={discountCode} discount={discount} onSuccess={handleSuccess} finalPrice={finalPrice} navigate={navigate} />
          </Box>
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default SubscriptionCheckout;
